import { getEnvVars } from '@order/utils';

import {
  addLocalImageSkip,
  applyOneTrustBannerA11yEnhancements,
  applyOneTrustFocusTrap,
  applyOneTrustPreferenceCenterA11yEnhancements,
  ONE_TRUST_CONSENT_CHANGED_EVENT,
} from './helpers';

// ─────────────────────────────────────────────────────────────────────────────

export function loadOneTrust() {
  // NOTE: If the `HideCookiesBanner` cookie is present, we should not load the
  //       OneTrust to prevent the banner from showing up.
  const shouldPreventLoading = document.cookie.includes('HideCookiesBanner');

  if (shouldPreventLoading) return;

  // We call the local-image skip observer BEFORE appending OneTrust's scripts
  // so it wins the race against OneTrust's auto-block scanner
  addLocalImageSkip();

  observeOneTrustBanner();

  const scripts = getOneTrustScripts();

  scripts.forEach((script) => {
    document.body.append(script);
  });
}

// ─────────────────────────────────────────────────────────────────────────────

function observeOneTrustBanner() {
  const existingBanner = oneTrustElements.getBanner();

  if (existingBanner) {
    applyOneTrustA11yEnhancements();

    return;
  }

  const observer = new MutationObserver(() => {
    const banner = oneTrustElements.getBanner();

    if (!banner) return;

    applyOneTrustA11yEnhancements();

    observer.disconnect();
  });

  observer.observe(document.body, { childList: true });
}

// ─────────────────────────────────────────────────────────────────────────────

function getOneTrustScripts() {
  const otAutoBlockScript = createOtAutoBlockScript();
  const otSDKStubScript = createOtSDKStubScript();
  const otOptAnonWrapper = createOtOptAnonWrapper();

  return [otAutoBlockScript, otSDKStubScript, otOptAnonWrapper].filter(
    Boolean,
  ) as HTMLScriptElement[];
}

function createOtAutoBlockScript() {
  if (!ONE_TRUST_SCRIPT_DOMAIN_KEY) return;

  return createScriptIfDoesntExist({
    id: 'ot-auto-block-script',
    src: `https://cdn.cookielaw.org/consent/${ONE_TRUST_SCRIPT_DOMAIN_KEY}/OtAutoBlock.js`,
  });
}

function createOtSDKStubScript() {
  if (!ONE_TRUST_SCRIPT_DOMAIN_KEY) return;

  const script = createScriptIfDoesntExist({
    id: 'ot-sdk-stub-script',
    src: 'https://cdn.cookielaw.org/scripttemplates/otSDKStub.js',
  });

  if (!script) return;

  script.dataset.domainScript = ONE_TRUST_SCRIPT_DOMAIN_KEY;

  return script;
}

function createOtOptAnonWrapper() {
  const script = createScriptIfDoesntExist({
    id: 'ot-opt-anon-wrapper-script',
  });

  // NOTE: OneTrust calls the wrapper once it loads and on every consent change.
  const code = `function OptanonWrapper() {
    window.dispatchEvent(new Event('${ONE_TRUST_CONSENT_CHANGED_EVENT}'));
  }`;

  if (!script) return;

  script.append(document.createTextNode(code));

  return script;
}

function createScriptIfDoesntExist(params: CreateScriptIfDoesntExistParams) {
  const { id, src } = params;

  const isScriptAlreadyAdded = checkIfScriptExists(id);

  if (isScriptAlreadyAdded) return;

  const script = document.createElement('script');

  script.id = id;

  if (src) {
    script.src = src;
  }

  return script;
}

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Checks if a script with the specified ID exists in the document.
 * Helpful for avoiding duplicated actions, which can cause HMR to fail.
 */
function checkIfScriptExists(id: string) {
  const maybeScript = document.querySelector(`script[id="${id}"]`);

  return maybeScript !== null;
}

// ─────────────────────────────────────────────────────────────────────────────

function applyOneTrustA11yEnhancements() {
  applyOneTrustBannerA11yEnhancements();
  applyOneTrustPreferenceCenterA11yEnhancements();
  applyOneTrustFocusTrap();
}

// ─── Constants ───────────────────────────────────────────────────────────────

const { ONE_TRUST_SCRIPT_DOMAIN_KEY } = getEnvVars();

const oneTrustElements = {
  getBanner: () => document.querySelector<HTMLElement>('#onetrust-consent-sdk'),
};

// ─── Types ───────────────────────────────────────────────────────────────────

type CreateScriptIfDoesntExistParams = { id: string; src?: string };
